// AGENT-A5: Dashboard top bar.
//
// 56px sticky header. Left: "HSQ Dashboard" h1 + breadcrumb beneath.
// Right: live-status pill (driven by useHealth, polls /api/health every 30s),
// "View Raw Tables" button (dispatches window event for Agent R's drawer),
// help-icon button placeholder, and a mobile sidebar toggle that's only
// visible on viewports <1100px.

import { useEffect } from "react";
import { useHealth } from "../../api/hooks";
import { useUiStore } from "../state/uiStore";
import { useFilterStore } from "../state/filterStore";
// AGENT-CX: source the legacy landing folder slug from the existing config
// so the "Legacy view" link tracks the central HSQ_LANDING_FOLDER constant.
import { HSQ_LANDING_FOLDER } from "../../config/hsqPages";
// AGENT-A3: read the live `headerStats` block from the shared /report
// react-query cache so the persistent header counts move in lockstep
// with every filter change.
import { useReportData } from "../charts/useReportData";
import "./TopBar.css";

// AGENT-A3: thousand-separator formatter for the persistent header counts.
const HEADER_COUNT_FMT = new Intl.NumberFormat("en-US");

export function TopBar() {
  const { data, status, isFetching } = useHealth();
  const toggleSidebar = useUiStore((s) => s.toggleSidebar);
  const toggleSidebarCollapsed = useUiStore((s) => s.toggleSidebarCollapsed);
  const theme = useUiStore((s) => s.theme);
  const toggleTheme = useUiStore((s) => s.toggleTheme);

  // The ≡ button means "show/hide the sidebar". On mobile (<1100px) the
  // sidebar is a slide-in drawer (sidebarOpen); on desktop it's a fixed
  // column, so we collapse it (sidebarCollapsed) to give the visuals full
  // width instead. Pick the right action by viewport at click time.
  const handleToggleSidebar = (): void => {
    if (typeof window !== "undefined" && window.matchMedia("(max-width: 1099px)").matches) {
      toggleSidebar();
    } else {
      toggleSidebarCollapsed();
    }
  };

  // Apply the theme to <html> so the dark `--dash-*` overrides cascade to
  // every dashboard surface — including portaled modals/drawers rendered
  // outside the .dash-layout subtree. Runs on mount + whenever theme flips.
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);
  const filterState = useFilterStore();
  const isDefaultFilters =
    filterState.categories.size === 0 &&
    filterState.parentProducts.size === 0 &&
    filterState.partNumbers.size === 0 &&
    filterState.vendors.size === 0 &&
    Object.keys(filterState.booleans).length === 0 &&
    filterState.online === "both" &&
    filterState.newOrExisting === "both" &&
    filterState.mode === "orders" &&
    filterState.dateRange.kind === "fy" &&
    filterState.dateRange.years.length === 0 &&
    filterState.viewLeft === "yoy" &&
    filterState.viewRight === "yoy" &&
    filterState.fyLeft.length === 0 &&
    filterState.fyRight.length === 0 &&
    filterState.pivotVisualId === "532f1cbdb173212cf1be";
  // AGENT-A3: persistent header counts — distinct INVOICE_ID by
  // INVOICE_TYPE on the Invoice frame, narrowed by the active filters.
  // Render "—" while loading, on error, or when the field is absent.
  const report = useReportData();
  const headerStats = report.data?.headerStats;
  const formatHeaderCount = (n: number | undefined | null): string => {
    if (report.isLoading || report.isError) return "—";
    if (typeof n !== "number" || !Number.isFinite(n)) return "—";
    return HEADER_COUNT_FMT.format(n);
  };

  let pillClass = "dash-topbar-pill is-unknown";
  let pillText = "Connecting…";
  if (status === "error") {
    pillClass = "dash-topbar-pill is-down";
    pillText = "Offline";
  } else if ((data as { ok?: boolean } | undefined)?.ok) {
    pillClass = "dash-topbar-pill is-ok";
    pillText = "Connected";
  } else if (data && (data as { status?: string }).status === "degraded") {
    pillClass = "dash-topbar-pill is-degraded";
    pillText = "Degraded";
  }

  const handleOpenRawTables = () => {
    window.dispatchEvent(new CustomEvent("hsq:open-raw-tables"));
  };

  return (
    <header className="dash-topbar">
      <div className="dash-topbar-left">
        <button
          type="button"
          className="dash-topbar-menu"
          aria-label="Show or hide the categories sidebar"
          title="Show / hide sidebar"
          onClick={handleToggleSidebar}
        >
          <span aria-hidden>≡</span>
        </button>
        <div className="dash-topbar-title-wrap">
          <h1 className="dash-h1 dash-topbar-title">HSQ Dashboard</h1>
          <div className="dash-topbar-breadcrumb dash-small">
            HSQ Report <span aria-hidden>›</span> Main View
          </div>
        </div>
      </div>

      <div className="dash-topbar-right">
        {/* AGENT-A3: persistent header counts — distinct INVOICE_ID by
            INVOICE_TYPE on Invoice, narrowed by active filters. Numbers
            formatted with Intl thousand separators; "—" while loading. */}
        <div className="dash-headline-stats" aria-live="polite">
          <div className="dash-stat-tile">
            <div className="dash-stat-tile__label">Quotes</div>
            <div className="dash-stat-tile__value">
              {formatHeaderCount(headerStats?.quotes)}
            </div>
          </div>
          <div className="dash-stat-tile">
            <div className="dash-stat-tile__label">Orders</div>
            <div className="dash-stat-tile__value">
              {formatHeaderCount(headerStats?.orders)}
            </div>
          </div>
        </div>
        {/* Reset Filters button hidden — FilterBar.tsx hosts the canonical
            "N filters active" + Reset control next to the mode toggles, so
            this duplicate is suppressed. Replace `{false && (` with `{(` to
            re-enable. */}
        {false && (
          <button
            type="button"
            className="dash-button"
            onClick={filterState.reset}
            disabled={isDefaultFilters}
            title="Reset all filters to defaults"
          >
            Reset Filters
          </button>
        )}
        <div
          className={pillClass}
          title={isFetching ? "Refreshing…" : pillText}
        >
          <span className="dash-topbar-pill-dot" />
          <span>{isFetching ? "…" : pillText}</span>
        </div>
        {/* AGENT-A3: "View Raw Tables" remains a no-op placeholder that
            dispatches the legacy event — Agent 10 wires the modal. */}
        <button
          type="button"
          className="dash-button"
          onClick={handleOpenRawTables}
          title="Browse the underlying source tables"
        >
          View Raw Tables
        </button>
        {/* AGENT-CX: "Legacy view" link — small, muted, font-size 12px so it
            doesn't compete with primary controls. Points at the legacy
            /p/{HSQ_LANDING_FOLDER} route which remains reachable. */}
        {/* Legacy view link hidden — replace `{false && (` with `{(` to re-enable */}
        {false && (
          <a
            href={`/p/${HSQ_LANDING_FOLDER}`}
            className="dash-small"
            style={{
              fontSize: 12,
              color: "var(--dash-text-muted, #6b7280)",
              textDecoration: "none",
              padding: "0 6px",
            }}
            title="Open the legacy HSQ page"
          >
            Legacy view
          </a>
        )}
        <button
          type="button"
          className="dash-button dash-topbar-theme"
          onClick={toggleTheme}
          aria-pressed={theme === "dark"}
          title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
          aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
        >
          <span aria-hidden>{theme === "dark" ? "☀" : "🌙"}</span>
        </button>
      </div>
    </header>
  );
}
